"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const React = __importStar(require("react"));
const useHotkey_1 = require("../useHotkey");
const styled_1 = __importDefault(require("../utils/styled"));
const Icon_1 = require("../Icon");
const mixins_1 = require("../utils/mixins");
const StepContent = styled_1.default("div") `
  label: StepContent;
  height: 100%;
  width: 100%;
`;
StepContent.displayName = "StepContent";
const StepLabel = styled_1.default("div") `
  label: StepLabel;
  min-width: fit-content;
  white-space: pre;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
`;
StepLabel.displayName = "StepLabel";
const Steps = styled_1.default("ul") `
  label: Steps;
  display: flex;
  justify-items: center;
  margin: 0 0 ${({ theme }) => theme.space.big}px;
`;
Steps.displayName = "Steps";
const ballSize = 24;
const translateY = ballSize / 2;
const Step = styled_1.default("li") `
  position: relative;
  width: 100%;
  color: ${({ theme, stepState }) => {
    if (stepState === "active")
        return theme.color.text.dark;
    if (stepState === "completed")
        return theme.color.text.lighter;
    return theme.color.text.lightest;
}};
  font-weight: ${({ theme, stepState }) => stepState === "active" ? theme.font.weight.bold : theme.font.weight.regular};
  text-align: center;
  overflow: hidden;
  outline: none;
  /** Separator line */
  ::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    margin: 0 auto;
    top: 0;
    transform: translateY(${translateY}px);
    display: block;
    height: 1px;
    width: 100%;
    background-color: ${({ theme }) => theme.color.border.default};
  }
  :focus {
    ${({ theme }) => mixins_1.inputFocus({ theme })};
  }

  :first-of-type::after,
  :last-of-type::after {
    width: 50%;
  }

  :first-of-type::after {
    transform: translate(50%, ${translateY}px);
  }

  :last-of-type::after {
    transform: translate(-50%, ${translateY}px);
  }
`;
Step.displayName = "Step";
const Ball = styled_1.default("div") `
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.space.small}px;
  width: ${ballSize}px;
  height: ${ballSize}px;
  border-radius: 50%;
  background-color: ${({ theme, stepState }) => {
    if (stepState === "active")
        return theme.color.primary;
    if (stepState === "completed")
        return theme.color.text.lightest;
    return theme.color.border.default;
}};
  font-weight: ${({ theme }) => theme.font.weight.regular};
  color: ${({ theme }) => theme.color.white};
  z-index: 1;
  :focus {
    color:  ${({ theme, stepState }) => stepState != "active" && stepState != "completed" ? theme.color.primary : theme.color.white};
    ${({ theme }) => mixins_1.inputFocus({ theme })},
  }
}`;
function getStepState(index, activeIndex) {
    if (activeIndex === undefined)
        return "inactive";
    if (index < activeIndex)
        return "completed";
    if (index === activeIndex)
        return "active";
    return "inactive";
}
const getNextTabIndex = (current, lastIndex) => {
    if (current + 1 > lastIndex)
        return 0;
    return current + 1;
};
const getPreviousTabIndex = (current, lastIndex) => {
    if (current - 1 < 0)
        return lastIndex;
    return current - 1;
};
const Stepper = props => {
    const { steps, stepColor, onStepChange, activeSlideIndex } = props, rest = __rest(props, ["steps", "stepColor", "onStepChange", "activeSlideIndex"]);
    const [focusedTab, setFocusedTab] = React.useState({
        index: 0,
        shouldFocus: false,
    });
    const clickHandler = React.useCallback((activeIndex) => {
        if (onStepChange) {
            onStepChange(activeIndex);
        }
    }, [onStepChange]);
    const hotkeyScope = React.useRef(null);
    useHotkey_1.useHotkey(hotkeyScope, { key: "ArrowLeft" }, () => {
        const index = getPreviousTabIndex(focusedTab.index, steps.length - 1);
        setFocusedTab({ index, shouldFocus: true });
    });
    useHotkey_1.useHotkey(hotkeyScope, { key: "ArrowRight" }, () => {
        const index = getNextTabIndex(focusedTab.index, steps.length - 1);
        setFocusedTab({ index, shouldFocus: true });
    });
    useHotkey_1.useHotkey(hotkeyScope, { key: "Enter" }, () => {
        clickHandler(focusedTab.index);
    });
    // Set actual focus on each render
    const $focusedTab = React.useRef(null);
    React.useEffect(() => {
        if ($focusedTab.current && focusedTab.shouldFocus) {
            $focusedTab.current.focus();
            setFocusedTab(Object.assign({}, focusedTab, { shouldFocus: false }));
        }
    }, [focusedTab]);
    // Set focus to the current slide if the value has changed
    React.useEffect(() => {
        if (activeSlideIndex !== undefined) {
            setFocusedTab({ index: activeSlideIndex, shouldFocus: false });
        }
    }, [activeSlideIndex]);
    return (React.createElement("div", Object.assign({ "data-cy": "operational-ui__Stepper" }, rest),
        React.createElement(Steps, { ref: hotkeyScope, steps: steps.length, "data-cy": "operational-ui__Stepper-steps", "aria-orientation": "horizontal", role: "tablist" }, steps.map(({ title }, index) => {
            const stepState = getStepState(index, activeSlideIndex);
            const number = index + 1;
            return (React.createElement(Step, { "data-cy": `operational-ui__Stepper__step-${index}`, ref: index === focusedTab.index ? $focusedTab : undefined, tabIndex: index === focusedTab.index ? 0 : -1, key: index, stepState: stepState, color: stepColor, onClick: () => clickHandler(index), role: "tab", "aria-selected": index === activeSlideIndex, "aria-setsize": steps.length, "aria-posinset": number },
                React.createElement(Ball, { stepState: stepState }, stepState === "completed" ? React.createElement(Icon_1.YesIcon, { size: 11 }) : number),
                title));
        })),
        React.createElement(StepContent, { "data-cy": "operational-ui__Stepper-content", role: "tabpanel" }, steps[activeSlideIndex].content)));
};
Stepper.defaultProps = {
    stepColor: "primary",
    activeSlideIndex: 0,
};
exports.default = Stepper;
//# sourceMappingURL=Stepper.js.map